import React, { ReactElement, useMemo, useCallback } from "react";
import { RecurrencePatternFormProps } from "./RecurrencePatternForm.types";
import { Dropdown, IDropdownOption } from "@fluentui/react";
import { SpintrTypes } from "src/typings";
import { localize } from "src/l10n";
import { ConditionalRender } from "../ConditionalRender";
import { UnstyledButton } from "src/ui";

function getRecurrenceTypeOptions(): IDropdownOption<Spintr.RecurrenceType>[] {
    return [
        { data: SpintrTypes.RecurrenceType.Daily
        , key: SpintrTypes.RecurrenceType.Daily
        , text: localize("DAILY")
        },
        { data: SpintrTypes.RecurrenceType.Weekly
        , key: SpintrTypes.RecurrenceType.Weekly
        , text: localize("WEEKLY")
        },
        { data: SpintrTypes.RecurrenceType.Monthly
        , key: SpintrTypes.RecurrenceType.Monthly
        , text: localize("MONTHLY")
        },
        { data: SpintrTypes.RecurrenceType.Yearly
        , key: SpintrTypes.RecurrenceType.Yearly
        , text: localize("YEARLY")
        },
    ];
}

const intervalOptions = [...Array(99)].map((_, i) => i + 1).map((n) => ({
    key: n,
    text: n.toString(),
    data: n,
}));

/**
 * RecurrencePatternForm is a React component that renders a form for selecting
 * a recurrence pattern.
 *
 * @param {RecurrencePatternFormProps} props - The props for the component.
 * @returns {ReactElement} The rendered component.
 */
function RecurrencePatternForm({
    onChange,
    referenceDate,
    value,
}: RecurrencePatternFormProps): ReactElement {
    const typeOptions = useMemo(getRecurrenceTypeOptions, []);

    const onIntervalChange = useCallback(
        (_: unknown, option?: IDropdownOption<number>) => {
            if (!option) {
                return;
            }

            onChange?.({ ...value, interval: option.data });
        },
        [onChange, value]);

    const onTypeChange = useCallback(
        (_: unknown, option?: IDropdownOption<Spintr.RecurrenceType>) => {
            if (!option) {
                return;
            }

            onChange?.({ ...value, type: option.data });
        },
        [onChange, value]);

    const renderDailyRecurrence = useCallback(() => (
        <div></div>
    ), []);

    const renderWeeklyRecurrence = useCallback(() => (
        <div></div>
    ), []);

    const renderMonthlyRecurrence = useCallback(() => (
        <div>
            <ul>
                <li>
                    <UnstyledButton>
                        {referenceDate.getDate()}
                    </UnstyledButton>
                </li>
            </ul>
        </div>
    ), []);

    const renderYearlyRecurrence = useCallback(() => (
        <div></div>
    ), []);

    return (
        <section className="RecurrencePatternForm">
            <Dropdown onChange={onIntervalChange}
                      options={intervalOptions}
                      selectedKey={value.interval} />
            <Dropdown onChange={onTypeChange}
                      options={typeOptions}
                      selectedKey={value.type} />

            <ConditionalRender condition={value.type === SpintrTypes.RecurrenceType.Daily}>
                {renderDailyRecurrence}
            </ConditionalRender>

            <ConditionalRender condition={value.type === SpintrTypes.RecurrenceType.Weekly}>
                {renderWeeklyRecurrence}
            </ConditionalRender>

            <ConditionalRender condition={value.type === SpintrTypes.RecurrenceType.Monthly}>
                {renderMonthlyRecurrence}
            </ConditionalRender>

            <ConditionalRender condition={value.type === SpintrTypes.RecurrenceType.Yearly}>
                {renderYearlyRecurrence}
            </ConditionalRender>
        </section>
    );
}

export default RecurrencePatternForm;

